/**
 * Carb timing advisory — pure rules per Burke 2017.
 *
 * Inputs: athlete body weight + workout duration + workout intensity.
 * Outputs: 4 fueling windows (T−3h, T−0:30, during, T+0:30) with
 * grams of carbs and suggested food types.
 *
 * Methodology refs (Burke L. 2017, "A Practical Guide to Sports Nutrition"):
 *  - Pre (3h):       1–4 g/kg depending on intensity; we use 0.5 g/kg
 *                    for light, 1 g/kg for moderate, 2 g/kg for hard
 *  - Pre (30min):    10–30 g fast-absorbing; we use 15 g for moderate+,
 *                    skip for light
 *  - During:         30–60 g/h moderate, 60–90 g/h hard endurance
 *                    (skipped when workout < 60 min)
 *  - Recovery:       1 g/kg + 20 g protein within 30 min
 */

import { carbsPerHour, intensityFactorOf } from "./fuelingLadder";

export type WorkoutIntensity = "light" | "moderate" | "hard";

export interface CarbTimingInput {
  /** kg */
  bodyWeightKg: number;
  /** minutes */
  durationMin: number;
  intensity: WorkoutIntensity;
}

export interface CarbWindow {
  key: "pre_3h" | "pre_30m" | "during" | "recovery";
  /** Label key for i18n (also defaults below) */
  timingKey: string;
  defaultTiming: string;
  carbGrams: number;
  /** Optional grams of protein (recovery window only). */
  proteinGrams?: number;
  foodHintKey: string;
  defaultFoodHint: string;
}

export interface CarbPlan {
  totalCarbGrams: number;
  windows: CarbWindow[];
}


function preWorkoutCarbs(weightKg: number, intensity: WorkoutIntensity): number {
  const perKg = intensity === "hard" ? 2.0 : intensity === "moderate" ? 1.0 : 0.5;
  return Math.round(weightKg * perKg);
}


function duringPerHour(intensity: WorkoutIntensity, durationMin: number): number {
  // Своей шкалы здесь больше нет: ступень берётся из общей лестницы,
  // иначе на трёхчасовой тяжёлой работе экраны расходились на 15 г/ч.
  return carbsPerHour(durationMin, intensityFactorOf(intensity));
}


export function computeCarbPlan(input: CarbTimingInput): CarbPlan {
  const windows: CarbWindow[] = [];
  const w = Math.max(40, Math.min(120, input.bodyWeightKg));

  // T−3h: bigger meal
  windows.push({
    key: "pre_3h",
    timingKey: "window_pre_3h",
    defaultTiming: "T−3:00",
    carbGrams: preWorkoutCarbs(w, input.intensity),
    foodHintKey:
      input.intensity === "hard"
        ? "food_pre_3h_hard"
        : input.intensity === "moderate"
          ? "food_pre_3h_moderate"
          : "food_pre_3h_light",
    defaultFoodHint:
      input.intensity === "hard"
        ? "Овсянка + банан + мёд + кофе"
        : input.intensity === "moderate"
          ? "Банан + овсянка"
          : "Лёгкий завтрак / фрукт",
  });

  // T−0:30: top-off (skip for light)
  if (input.intensity !== "light") {
    windows.push({
      key: "pre_30m",
      timingKey: "window_pre_30m",
      defaultTiming: "T−0:30",
      carbGrams: 15,
      foodHintKey: "food_pre_30m",
      defaultFoodHint: "Гель / 1 банан / 200 мл изотоника",
    });
  }

  // During: only if duration ≥ 60 min and intensity moderate+
  if (input.durationMin >= 60 && input.intensity !== "light") {
    const perHour = duringPerHour(input.intensity, input.durationMin);
    const hours = input.durationMin / 60;
    const totalDuring = Math.round(perHour * hours);
    windows.push({
      key: "during",
      timingKey: "window_during",
      defaultTiming: "ВО ВРЕМЯ",
      carbGrams: totalDuring,
      foodHintKey: input.intensity === "hard" ? "food_during_hard" : "food_during_moderate",
      defaultFoodHint:
        input.intensity === "hard"
          ? "Гели каждые 25–30 мин + изотоник"
          : "Гель каждые 45 мин или энергобатончик",
    });
  }

  // Recovery: always within 30 min for moderate+
  if (input.intensity !== "light" || input.durationMin >= 60) {
    windows.push({
      key: "recovery",
      timingKey: "window_recovery",
      defaultTiming: "T+0:30",
      carbGrams: Math.round(w * 1.0),
      proteinGrams: 20,
      foodHintKey: "food_recovery",
      defaultFoodHint: "Recovery drink или йогурт + банан + орехи",
    });
  }

  const totalCarbGrams = windows.reduce((sum, win) => sum + win.carbGrams, 0);
  return { totalCarbGrams, windows };
}
